import React, {useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {useDispatch, useSelector} from 'react-redux';

import {useMediaQuery} from 'src/hooks/useMediaQuery';
import {Store} from 'src/types/store';
import {UserType} from 'src/types/enums';

import Icon from 'shared/components/icon';
import {uploadProfilePicture} from 'shared/actions/avatar';

import 'stylesheets/components/avatar-dropzone.scss';

interface Props {
  image: string;
  userType: UserType;
  editing: boolean;
}

const Avatar = ({image, userType, editing = false}: Props) => {
  const dispatch = useDispatch();
  const uploading = useSelector((state: Store) => state.avatar.uploading);
  const mq = useMediaQuery();
  const [hover, setHover] = useState(false);

  const uploadImage = ([file]: File[]) => {
    if (!file) {
      return;
    }

    dispatch(uploadProfilePicture(userType, file));
  };

  const {getRootProps, getInputProps} = useDropzone({
    accept: {'image/*': []},
    multiple: false,
    onDrop: uploadImage,
  });

  const renderOverlay = () => {
    if (!editing && !uploading && !hover) {
      return null;
    }

    return (
      <div className="avatar-overlay">
        <div className="avatar-overlay-image">
          {uploading ? (
            <Icon iconType="spinner" className="avatar-spinner rm-spin" />
          ) : (
            <div>
              <svg
                width="28px"
                height="34px"
                viewBox="0 0 28 34"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g transform="translate(-229.000000, -183.000000)" fill="#FFFFFF">
                    <path d="M237,209 L249,209 L249,197 L257,197 L243,183 L229,197 L237,197 L237,209 Z M229,213 L257,213 L257,217 L229,217 L229,213 Z"></path>
                  </g>
                </g>
              </svg>
            </div>
          )}
        </div>
        {uploading || mq.SM_AND_DOWN ? null : (
          <p className="avatar-text">Change your profile photo</p>
        )}
      </div>
    );
  };

  const size = mq.SM_AND_DOWN ? 100 : 174;

  return (
    <div
      {...getRootProps()}
      className="avatar-dropzone"
      onMouseEnter={mq.SM_AND_DOWN ? undefined : () => setHover(true)}
      onFocus={mq.SM_AND_DOWN ? undefined : () => setHover(true)}
      onMouseLeave={mq.SM_AND_DOWN ? undefined : () => setHover(false)}
      onBlur={mq.SM_AND_DOWN ? undefined : () => setHover(false)}
    >
      <input {...getInputProps()} />
      {renderOverlay()}
      <img
        alt="User avatar"
        className="avatar-dropzone-image"
        src={image}
        width={size}
        height={size}
      />
    </div>
  );
};

export default Avatar;
